import React, { useEffect, useState, useMemo, useContext } from 'react';
import { Card, Button, Snackbar, Alert } from '@mui/material';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import StageCommitTable from '../tabels/StageCommitTable';
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';
import ArgonInput from 'components/ArgonInput';
import { commitProductsToDatabase } from 'services/product';
import { useNotifications } from 'context/NotificationContext'; // Import notification hook
import { createActivity } from 'services/activity';
import { RecentContext } from 'context/recentContext';

const calculateNewVersion = (baseVersion = '0.0.0', versionType) => {
  const parts = baseVersion.split('.').map(Number);
  if (parts.length !== 3 || parts.some(isNaN)) {
    return versionType === 'new' ? '1.0.0' : '0.0.1'; // Fallback for invalid formats
  }

  if (versionType === 'new') {
    parts[0] += 1;
    parts[1] = 0;
    parts[2] = 0;
  } else {
    // 'patch'
    parts[2] += 1;
  }
  return parts.join('.');
};

const withActivityLogging = async (operationFn, baseActivity, addNotification) => {
  // 1. Log "In Progress" status (fire and forget)
  createActivity([{ ...baseActivity, status: 'In Progress' }]).catch((err) =>
    console.error('Failed to log "In Progress" activity:', err.message)
  );

  try {
    // 2. Execute the main operation
    const result = await operationFn();

    // 3. Log "Success" status (fire and forget)
    createActivity([{ ...baseActivity, status: 'Success', view_details: JSON.stringify(result) }]).catch((err) =>
      console.error('Failed to log "Success" activity:', err.message)
    );

    return result; // Return the result of the successful operation
  } catch (error) {
    // 4. Log "Failed" status (fire and forget)
    createActivity([
      { ...baseActivity, status: 'Failed', view_details: JSON.stringify({ error: error.message }) },
    ]).catch((err) => console.error('Failed to log "Failed" activity:', err.message));

    // 5. Re-throw the original error to be handled by the component
    throw error;
  }
};

const CommitButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.info.main,
  color: theme.palette.common.white,
  '&:hover': {
    backgroundColor: theme.palette.info.dark,
  },
}));

const BackButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.secondary.main,
  color: theme.palette.common.white,
  '&:hover': {
    backgroundColor: theme.palette.secondary.dark,
  },
}));

function StageCommit({ userId, org, commitProducts, handleStageChange, versionType }) {
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null); // Keep for Snackbar
  const [successMessage, setSuccessMessage] = useState(null); // Keep for Snackbar
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const { addNotification } = useNotifications(); // Get notification function
  const [commitSuccessful, setCommitSuccessful] = useState(false); // State to track commit success
  const { addRecentProduct } = useContext(RecentContext);

  useEffect(() => {
    setSelectedColumns([
      { name: 'product', align: 'left' },
      { name: 'product_version', align: 'center' },
      { name: 'new_version', align: 'center' },
    ]);
  }, [commitProducts]);

  const filteredCommitProducts = useMemo(() => {
    if (!searchTerm) {
      return commitProducts;
    }
    const lowerSearchTerm = searchTerm.toLowerCase();
    return commitProducts.filter((product) => {
      const productName = product.name ? product.name.toLowerCase() : '';
      const productVersion = product.database_version ? product.database_version.toLowerCase() : '';
      return productName.includes(lowerSearchTerm) || productVersion.includes(lowerSearchTerm);
    });
  }, [commitProducts, searchTerm]);

  const tableRows = useMemo(() => {
    if (!Array.isArray(filteredCommitProducts)) {
      return [];
    }
    return filteredCommitProducts.map((product) => {
      const productId = product.sfId || product.external_id || product.id || product.sf_id;
      let newVersion = '';
      let baseVersion = product.database_version || product.sf_product?.product_version || '0.0.0';

      if (baseVersion) {
        const versionParts = baseVersion.split('.').map(Number);
        if (versionParts.length === 3) {
          if (versionType === 'new') {
            newVersion = `${versionParts[0] + 1}.0.0`;
          } else {
            newVersion = `${versionParts[0]}.${versionParts[1]}.${versionParts[2] + 1}`;
          }
        } else {
          newVersion = 'Invalid Version Format';
        }
      } else {
        newVersion = versionType === 'publish' ? '1.0.0' : '0.0.1';
      }

      return {
        product: (
          <ArgonTypography ml={3} variant="caption" color="secondary" fontWeight="bold">
            {product.name || 'Unknown Product'}
          </ArgonTypography>
        ),
        product_version: (
          <ArgonTypography variant="caption" color="secondary" fontWeight="medium">
            {product.database_version || 'N/A'}
          </ArgonTypography>
        ),
        new_version: (
          <ArgonTypography variant="caption" color="info" fontWeight="bold">
            {newVersion}
          </ArgonTypography>
        ),
        id: productId,
      };
    });
  }, [filteredCommitProducts, versionType]);

  useEffect(() => {
    setSelectedRows(tableRows);
  }, [tableRows]);

  const commitChanges = async () => {
    if (!commitProducts || commitProducts.length === 0) {
      return;
    }

    setLoading(true);

    // 1. Prepare the API payload using the new helper
    const commitPromises = commitProducts.map((product) => {
      // a. Prepare the payload for this single product.
      const baseVersion = product.database_version || product.sf_product?.product_version;
      const newProductVersion = calculateNewVersion(baseVersion, versionType);
      const productPayload = {
        ...(product.sf_product || {}),
        id: product.sfId || product.external_id || product.id || product.sf_product?.id,
        name: product.name || product.sf_product?.name,
        product_version: newProductVersion,
        created_date: new Date().toISOString(),
      };

      // b. Define the core API call for this single product.
      const operation = () => commitProductsToDatabase(org.org_id, [productPayload]);

      // c. Define the base activity log data for this single product.
      const activity = {
        operation_type: 'Commit',
        org_name: org.name,
        product_name: productPayload.name,
        product_version: newProductVersion,
        status: 'In Progress', // Initial status
      };

      // d. Wrap the operation in our logging helper.
      return withActivityLogging(operation, activity);
    });

    try {
      // 2. Execute all the individual commit promises.
      // Promise.allSettled is better here because it won't stop if one product fails.
      const results = await Promise.allSettled(commitPromises);

      // 3. Process the results to provide a final summary notification.
      const successfulCommits = results.filter((r) => r.status === 'fulfilled');
      const failedCommits = results.filter((r) => r.status === 'rejected');

      if (failedCommits.length > 0) {
        const failedNames = commitProducts
          .filter((p, i) => results[i].status === 'rejected')
          .map((p) => p.name)
          .join(', ');

        const errorMessage = `Commit failed for: ${failedNames}.`;
        setError(errorMessage);
        setOpenSnackbar(true);
        addNotification({ title: ['Error', 'Commit Partially Failed'], severity: 'error', details: errorMessage });
      } else {
        const successMsg = `Successfully committed ${successfulCommits.length} product(s)!`;
        setSuccessMessage(successMsg);
        setOpenSnackbar(true);
        addNotification({ title: ['Success', 'Commit Complete'], severity: 'success', details: successMsg });

        // Track committed products as recent
        console.log('Commit org:', org);
        commitProducts.forEach((product) => {
          console.log('Adding recent product:', product, 'with org:', org);
          addRecentProduct(product, org);
        });

        setCommitSuccessful(true); // Set success state to true
        // Navigate back to the start after a short delay
        setTimeout(() => {
          handleStageChange('environment', { isSuccessReset: true });
        }, 2000);
      }
    } catch (err) {
      // This is for unexpected errors, not individual commit failures.
      const errorMessage = err.message || 'A critical error occurred during the commit process.';
      setError(errorMessage);
      setOpenSnackbar(true);
      addNotification({ title: ['Error', 'Commit Exception'], severity: 'error', details: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  return (
    <ArgonBox py={3}>
      <ArgonBox mb={3} display="flex" justifyContent="flex-end">
        <ArgonInput
          type="text"
          placeholder="Search products..."
          value={searchTerm}
          onChange={handleSearchChange}
          sx={{ width: '20rem' }}
        />
      </ArgonBox>
      <ArgonBox mb={3}>
        {commitProducts && commitProducts.length > 0 && (
          <Card>
            <ArgonBox p={3}>
              {selectedColumns.length > 0 && selectedRows.length > 0 ? (
                <StageCommitTable columns={selectedColumns} rows={selectedRows} />
              ) : (
                <ArgonTypography variant="body1">No products to display.</ArgonTypography>
              )}
            </ArgonBox>
          </Card>
        )}
      </ArgonBox>
      <ArgonBox mt={3} display="flex" justifyContent="space-between" gap={2}>
        <BackButton variant="contained" onClick={() => handleStageChange('review')}>
          Back to Review
        </BackButton>
        <CommitButton
          variant="contained"
          onClick={commitChanges}
          disabled={loading || !commitProducts || commitProducts.length === 0 || commitSuccessful}
          sx={
            commitSuccessful
              ? {
                  backgroundColor: 'success.main',
                  '&:hover': { backgroundColor: 'success.dark' },
                }
              : {}
          }
        >
          {loading ? 'Committing...' : commitSuccessful ? 'Committed Successfully' : 'Commit Changes'}
        </CommitButton>
      </ArgonBox>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          variant="filled"
          severity={successMessage ? 'success' : 'error'}
          sx={{ width: '100%' }}
        >
          {successMessage || error}
        </Alert>
      </Snackbar>
    </ArgonBox>
  );
}

StageCommit.propTypes = {
  userId: PropTypes.string.isRequired,
  org: PropTypes.object.isRequired,
  commitProducts: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      database_version: PropTypes.string,
      sfId: PropTypes.string,
      external_id: PropTypes.string,
      id: PropTypes.string,
      sf_product: PropTypes.shape({
        product_version: PropTypes.string,
      }).isRequired,
    })
  ),
  handleStageChange: PropTypes.func.isRequired,
  versionType: PropTypes.string.isRequired,
};

export default StageCommit;
